<!-- A real <button>, so it is focusable and activatable without any of the
     tabindex/keydown scaffolding a div would need. Its accessible name is built
     with aria-labelledby from two elements — the label and the current value —
     so it announces "Status, Needs Info" the way the native <select> it
     replaced did. A plain aria-label here would override the button's contents
     and leave the applied filter unannounced. -->
<button
  #trigger
  type="button"
  class="dropdown__trigger"
  [class.dropdown__trigger--open]="isOpen()"
  [disabled]="disabled()"
  role="combobox"
  aria-haspopup="listbox"
  [attr.aria-expanded]="isOpen()"
  [attr.aria-controls]="isOpen() ? panelId : null"
  [attr.aria-activedescendant]="activeOptionId()"
  [attr.aria-labelledby]="labelledBy()"
  (click)="toggle()"
  (keydown)="onTriggerKeydown($event)"
>
  <span class="dropdown__value" [id]="valueId">{{ displayValue() }}</span>
  <app-icon
    class="dropdown__caret"
    name="arrow_drop_down"
    size="sm"
    color="custom"
    customColor="currentColor"
    role="presentation"
  />
</button>

<!-- After the trigger so the open/focus states can reach it with a sibling
     combinator (same arrangement as app-input's label). Hidden from AT only
     when ariaLabel supplies a more specific name in its place, so the name is
     never announced twice. -->
<span class="dropdown__label" [id]="labelId" [attr.aria-hidden]="ariaLabel() ? 'true' : null"
  >{{ label() }}</span
>

@if (ariaLabel(); as spokenLabel) {
<span class="sr-only" [id]="ariaLabelId">{{ spokenLabel }}</span>
}

<!-- Rendered only while open: an always-present hidden listbox would still be
     reachable by AT and would announce options the user can't currently see. An
     empty options list renders the panel with no rows rather than failing.
     max-height is measured on open rather than fixed in CSS — see panelMaxHeight. -->
@if (isOpen()) {
<ul
  class="dropdown__panel"
  [id]="panelId"
  role="listbox"
  [attr.aria-label]="label()"
  [style.max-height.px]="panelMaxHeight()"
>
  @for (option of options(); track $index; let index = $index) {
  <!-- The rows are deliberately neither focusable nor key-handling: in the ARIA
       combobox/listbox pattern focus stays on the trigger, which owns the
       keyboard (arrows to move, Enter to choose) and names the highlighted row
       through aria-activedescendant. A tabindex here would put dead tab stops in
       the panel and contradict activedescendant, so the lint rules below are
       reporting a keyboard path that exists, just not on this element. -->
  <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
  <li
    class="dropdown__option"
    [id]="optionId(index)"
    role="option"
    [attr.aria-selected]="isSelected(option)"
    [attr.aria-disabled]="option.disabled ? 'true' : null"
    [class.dropdown__option--selected]="isSelected(option)"
    [class.dropdown__option--active]="index === activeIndex()"
    [class.dropdown__option--disabled]="option.disabled"
    (click)="selectOption(option)"
  >
    <span class="dropdown__option-label">{{ option.label }}</span>
    @if (isSelected(option)) {
    <app-icon
      class="dropdown__check"
      name="check"
      size="xs"
      color="custom"
      customColor="currentColor"
      role="presentation"
    />
    }
  </li>
  }
</ul>
}
